// The icon an app gets when the merchant skips the logo in the onboarding
// ("Preencher depois"): the app name's initial, white on the brand's
// graphite, so nobody mistakes it for the store's logo. It is a real 1024px
// PNG, the same the logo step uploads, so the rest of the flow (guards,
// complete-onboarding) needs nothing else; the merchant swaps it later in
// "Seu app" while the app is under construction.

const SIZE_PX = 1024;
const BACKGROUND_HEX = '#15191E';
const FOREGROUND_HEX = '#FFFFFF';
// Cap height around 40% of the icon, like a monogram app icon.
const FONT = `600 560px system-ui, -apple-system, "Segoe UI", sans-serif`;

/** First letter or digit of the name, upper-cased ('' when there is none). */
export function placeholderIconInitial(appName: string): string {
  const match = appName.normalize('NFC').match(/[\p{L}\p{N}]/u);
  return match ? match[0].toLocaleUpperCase('pt-BR') : '';
}

/** Draws the placeholder icon for `appName` as a 1024×1024 PNG. */
export async function renderPlaceholderAppIcon(appName: string): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE_PX;
  canvas.height = SIZE_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D is unavailable');

  ctx.fillStyle = BACKGROUND_HEX;
  ctx.fillRect(0, 0, SIZE_PX, SIZE_PX);

  const initial = placeholderIconInitial(appName);
  if (initial) {
    ctx.fillStyle = FOREGROUND_HEX;
    ctx.font = FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // Centred on the glyph's own ink, not the font's line box, so letters
    // with and without descenders sit in the middle alike.
    const metrics = ctx.measureText(initial);
    const inkHeight =
      metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
    const baseline =
      (SIZE_PX - inkHeight) / 2 + metrics.actualBoundingBoxAscent;
    const inkOffset =
      (metrics.actualBoundingBoxRight - metrics.actualBoundingBoxLeft) / 2;
    ctx.fillText(initial, SIZE_PX / 2 - inkOffset, baseline);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) {
        reject(new Error('Failed to encode the placeholder icon as PNG'));
        return;
      }
      resolve(new File([blob], 'app-logo.png', { type: 'image/png' }));
    }, 'image/png');
  });
}
